import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import chevronUpIcon from '@/assets/images/chevronUp.svg';
import { Price } from '@/components/Price/Price';
import { Position } from '@/interfaces/Bill';
import styles from '../Bill.module.scss';

interface Props {
  positions: Position[];
}

/**
 * The aggregated order rendering — one row per position with its quantity —
 * used both with no split and with an equal split active.
 */
export const BillPositions = ({ positions }: Props) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const toggleItem = useCallback((id: string) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  return (
    <div className={styles.items}>
      {positions.map((position: Position, index: number) => (
        <div className={styles.positionSection} key={position.id}>
          <div className={styles.itemRow}>
            <div className={styles.quantity} data-testid={`item-${index}-quantity`}>
              {t('checkout.bill.quantity', { quantity: position.quantity })}
            </div>
            <div data-testid={`item-${index}-name`}>
              {position.name}
              {position?.modifiersV2?.length > 0 && (
                <button
                  className={`${styles.chevron} ${expandedItems[position.id] ? styles.open : ''}`}
                  onClick={() => {
                    toggleItem(position.id);
                  }}
                >
                  <img src={chevronUpIcon} alt={t('checkout.bill.collapseIcon')} />
                </button>
              )}
            </div>
            <div className={styles.item} data-testid={`item-${index}-price`}>
              <Price amount={position.unitPrice * position.quantity} />
            </div>
          </div>
          <div
            className={`${styles.modifiersRow} ${expandedItems[position.id] ? styles.expanded : ''}`}
          >
            {position.modifiersV2?.map(({ name, quantity, unitPrice }) => (
              <div key={name} className={styles.modifier}>
                <div className={styles.modifierName}>
                  {name}
                  {quantity > 1 ? t('checkout.bill.modifierQuantity', { quantity }) : ''}
                </div>
                <div className={styles.modifierPrice}>
                  {unitPrice ? <Price amount={unitPrice * quantity} /> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
